import { useMemo, useState } from "react";
import { Link } from "wouter";
import { useSystemOptions } from "@/api/hooks";
import { adjacentPanelCount } from "@/utils/planViewModel";
import { gbp } from "@/utils/phoenixFinanceExample";
import BatterySizePicker from "./BatterySizePicker";
import ExampleRoofLayout from "./ExampleRoofLayout";
import { AI_PICK_BATTERY_KWH, EXAMPLE_BATTERY_KWH } from "./batteryOptions";
import {
  batteriesForPanelCount,
  pickAiExampleOption,
  pricedExampleOptions,
  snapExampleOption,
  uniqueExamplePanelCounts,
  type ExampleSystemOption,
} from "./examplePlanCatalog";
import { computePlan, DEFAULT_INPUTS, PlanInputs } from "./examplePlanModel";
import { BASELINE, BEST_SETUP, clampPillar, overallScore, bandFor, Pillars } from "./exampleScore";
import ScoreBreakdown from "./ScoreBreakdown";

function batteryBoostFor(battery: number) {
  if (battery <= 0) return 0;
  return Math.round(battery * (42 / AI_PICK_BATTERY_KWH));
}
function panelBoostFor(panels: number) {
  return Math.min(30, (panels - 6) * 2.5);
}
function futureBoostFor(ev: boolean, heatPump: boolean) {
  return (ev ? 15 : 0) + (heatPump ? 10 : 0);
}

export default function ExamplePlanCard() {
  const systemOptions = useSystemOptions();
  const options = useMemo(() => pricedExampleOptions(systemOptions.data), [systemOptions.data]);
  const aiOption = useMemo(() => pickAiExampleOption(options), [options]);

  if (!systemOptions.isFetched) return <ExamplePlanLoading />;

  return <ExamplePlanCardBody options={options} aiOption={options.length > 0 ? aiOption : null} />;
}

function ExamplePlanCardBody({
  options,
  aiOption,
}: {
  options: ExampleSystemOption[];
  aiOption: ExampleSystemOption | null;
}) {
  const usingCatalog = aiOption != null;
  const [inputs, setInputs] = useState<PlanInputs>(() =>
    aiOption
      ? { ...DEFAULT_INPUTS, panels: aiOption.panels, battery: aiOption.batteryKwh }
      : DEFAULT_INPUTS,
  );
  const [selectedId, setSelectedId] = useState<number | null>(aiOption?.id ?? null);
  const [isCustom, setIsCustom] = useState(false);
  const [scoreOpen, setScoreOpen] = useState(false);

  const selected = useMemo(() => {
    if (!usingCatalog) return null;
    const preferred = options.find((option) => option.id === selectedId);
    if (
      preferred &&
      preferred.panels === inputs.panels &&
      Math.abs(preferred.batteryKwh - inputs.battery) < 0.06
    ) {
      return preferred;
    }
    return snapExampleOption(options, inputs.panels, inputs.battery);
  }, [usingCatalog, options, selectedId, inputs.panels, inputs.battery]);

  const displayPanels = selected?.panels ?? inputs.panels;
  const displayBattery = selected?.batteryKwh ?? inputs.battery;
  const panelCounts = useMemo(() => uniqueExamplePanelCounts(options), [options]);
  const fewerPanels = usingCatalog ? adjacentPanelCount(panelCounts, displayPanels, "down") : null;
  const morePanels = usingCatalog ? adjacentPanelCount(panelCounts, displayPanels, "up") : null;
  const batteryChoices = usingCatalog ? batteriesForPanelCount(options, displayPanels) : [...EXAMPLE_BATTERY_KWH];
  const baselinePanels = aiOption?.panels ?? DEFAULT_INPUTS.panels;
  const baselineBattery = aiOption?.batteryKwh ?? DEFAULT_INPUTS.battery;

  const plan = useMemo(
    () =>
      computePlan(
        { ...inputs, panels: displayPanels, battery: displayBattery },
        selected
          ? {
              cost: selected.cost,
              annualBenefit: selected.annualBenefit,
              systemOutputW: selected.systemOutputW,
            }
          : null,
      ),
    [inputs, displayPanels, displayBattery, selected],
  );

  // The AI pick always shows the real, PDF-verified BEST_SETUP (~73/Strong).
  // Once the customer starts customising, the score moves as a delta from that
  // package, so returning to it lands on 73 again.
  const pillars: Pillars = useMemo(() => {
    if (!isCustom) return BEST_SETUP;

    const batteryBoost = batteryBoostFor(displayBattery);
    const panelBoost = panelBoostFor(displayPanels);
    const futureBoost = futureBoostFor(inputs.ev, inputs.heatPump);

    const defaultBatteryBoost = batteryBoostFor(baselineBattery);
    const defaultPanelBoost = panelBoostFor(baselinePanels);
    const defaultFutureBoost = futureBoostFor(DEFAULT_INPUTS.ev, DEFAULT_INPUTS.heatPump);

    return {
      // Fixed property facts — the recommended system never changes these.
      generation: BASELINE.generation,
      property: BASELINE.property,
      // Move as deltas from the AI-pick reference point.
      economic: clampPillar(
        BEST_SETUP.economic +
          (panelBoost - defaultPanelBoost) +
          (batteryBoost - defaultBatteryBoost) * 0.3 +
          (futureBoost - defaultFutureBoost) * 0.5
      ),
      independence: clampPillar(
        BEST_SETUP.independence + (panelBoost - defaultPanelBoost) * 0.6 + (batteryBoost - defaultBatteryBoost) * 0.5
      ),
      smartReady: clampPillar(
        BEST_SETUP.smartReady +
          ((displayBattery > 0 ? 35 : 5) - (baselineBattery > 0 ? 35 : 5))
      ),
      systemFit: clampPillar(BEST_SETUP.systemFit + (batteryBoost - defaultBatteryBoost) + (futureBoost - defaultFutureBoost) * 0.3),
    };
  }, [inputs.ev, inputs.heatPump, isCustom, displayPanels, displayBattery, baselinePanels, baselineBattery]);

  const overall = overallScore(pillars);
  const band = bandFor(overall);

  function update<K extends keyof PlanInputs>(key: K, value: PlanInputs[K]) {
    setIsCustom(true);
    setInputs((prev) => ({ ...prev, [key]: value }));
  }

  function choosePackage(panels: number, batteryKwh: number) {
    const row = snapExampleOption(options, panels, batteryKwh);
    if (!row) return;
    setIsCustom(true);
    setSelectedId(row.id);
    setInputs((prev) => ({ ...prev, panels: row.panels, battery: row.batteryKwh }));
  }

  function reset() {
    setInputs(
      aiOption
        ? { ...DEFAULT_INPUTS, panels: aiOption.panels, battery: aiOption.batteryKwh }
        : DEFAULT_INPUTS,
    );
    setSelectedId(aiOption?.id ?? null);
    setIsCustom(false);
  }

  return (
    <div className="rounded-[32px] p-4 dotted-indigo sm:p-8">
      <div className="overflow-hidden rounded-3xl bg-white">
        {/* header */}
        <div className="flex flex-col gap-3 border-b border-navyLight bg-navy px-6 py-4 text-white sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <div className="flex flex-col gap-1">
            <span className="font-display text-xl font-bold tracking-tight sm:text-2xl">Your Powlo Energy Plan</span>
            <span className="text-sm text-[#C9D1DE]">
              <span className="font-bold text-lime">Hello</span>
              {" · Example: detached home, South East"}
            </span>
          </div>
          {isCustom ? (
            <div className="flex items-center gap-2">
              <span className="rounded-full border border-navyBorder bg-navyLight px-4 py-2 text-sm font-bold">
                Your custom setup
              </span>
              <button onClick={reset} className="min-h-[40px] rounded-full bg-lime px-4 text-sm font-bold text-navy">
                Reset to AI pick
              </button>
            </div>
          ) : (
            <span className="flex items-center gap-2 rounded-full border border-navyBorder bg-navyLight px-4 py-2 text-sm font-bold">
              <StarIcon /> AI-recommended setup
            </span>
          )}
        </div>

        {/* score toggle */}
        <div className="bg-navy text-white">
          <button
            type="button"
            onClick={() => setScoreOpen(!scoreOpen)}
            aria-expanded={scoreOpen}
            className="flex w-full flex-col gap-3 px-4 py-4 text-left sm:flex-row sm:items-center sm:gap-4 sm:px-8 sm:py-5"
          >
            <span className="flex min-w-0 items-center gap-3 sm:gap-4">
              <MiniRing score={overall} color={band.color} />
              <span className="min-w-0 flex-1">
                <span className="block font-display text-base font-bold leading-tight tracking-tight sm:text-lg">
                  Home Power Score
                </span>
                <span className="text-sm leading-snug text-[#8FA0B8]">
                  <strong style={{ color: band.color }}>{band.label}</strong> &middot; six weighted pillars
                </span>
              </span>
            </span>
            <span className="flex w-full items-center justify-center gap-2 rounded-full border border-navyBorder px-4 py-2 text-sm font-bold text-[#C9D1DE] sm:w-auto sm:shrink-0">
              {scoreOpen ? "Hide" : "Show breakdown"}
              <Chevron open={scoreOpen} />
            </span>
          </button>
          {scoreOpen && (
            <div className="grid grid-cols-1 gap-6 px-4 pb-8 sm:px-8 lg:grid-cols-[200px_1fr]">
              <div className="flex flex-col items-center gap-3 lg:items-start">
                <span className="rounded-full border border-lime px-3 py-1 text-xs font-bold tracking-widest text-lime">
                  PERSONALISED
                </span>
                <BigRing score={overall} color={band.color} />
              </div>
              <ScoreBreakdown pillars={pillars} overall={overall} theme="dark" />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-6 p-6 sm:p-8">
          <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
            <div className="contents lg:flex lg:min-w-0 lg:flex-col lg:gap-6">
              <div className="relative order-1 flex min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-cream lg:order-none">
                <ExampleRoofLayout panels={displayPanels} />
                <div className="flex flex-col gap-1 px-4 py-3 text-left">
                  <div className="font-display text-lg font-bold">
                    {displayPanels} panels · {plan.kwp} kWp
                    {displayBattery > 0 ? ` · ${displayBattery} kWh battery` : ""}
                  </div>
                  <div className="text-sm text-muted">Example address in Sussex · Detached house</div>
                </div>
              </div>
              <div className="order-3 flex flex-col gap-4 lg:order-none">
                <div className="rounded-2xl bg-lavender p-4 text-sm leading-relaxed">
                  <strong>Recommended: </strong>
                  this roof faces west-south-west, so we sized the system for afternoon-into-evening generation
                  and paired it with a battery to cover your evening peak.
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <Stat label="Annual benefit" value={gbp(plan.annualBenefit)} sub={`${gbp(plan.monthlyBenefit)}/mo`} dark />
                  <Stat label="Payback" value={`${plan.paybackYears} yrs`} sub="Then it's profit" />
                  <Stat label="Independence" value={`${plan.independencePct}%`} sub="Of your electricity" />
                </div>
              </div>
            </div>
            <div className="order-2 flex min-w-0 flex-col gap-5 rounded-2xl border border-border p-5 lg:order-none">
              <div className="font-display text-lg font-bold tracking-tight">Customise your system</div>
              <div className="flex items-center justify-between gap-3">
                <span className="font-bold">Solar panels</span>
                <div className="flex items-center gap-2 rounded-2xl bg-[#F1F0EC] p-1">
                  <button
                    type="button"
                    aria-label="Fewer panels"
                    disabled={usingCatalog ? fewerPanels == null : displayPanels <= 4}
                    onClick={() =>
                      usingCatalog
                        ? fewerPanels != null && choosePackage(fewerPanels, displayBattery)
                        : update("panels", Math.max(4, displayPanels - 1))
                    }
                    className="h-10 w-10 rounded-xl bg-white text-xl font-bold disabled:opacity-40"
                  >
                    −
                  </button>
                  <div className="w-10 text-center font-display text-xl font-bold">{displayPanels}</div>
                  <button
                    type="button"
                    aria-label="More panels"
                    disabled={usingCatalog ? morePanels == null : displayPanels >= 24}
                    onClick={() =>
                      usingCatalog
                        ? morePanels != null && choosePackage(morePanels, displayBattery)
                        : update("panels", Math.min(24, displayPanels + 1))
                    }
                    className="h-10 w-10 rounded-xl bg-white text-xl font-bold disabled:opacity-40"
                  >
                    +
                  </button>
                </div>
              </div>
              <BatterySizePicker
                compact
                choices={batteryChoices}
                value={displayBattery}
                onChange={(kwh) => (usingCatalog ? choosePackage(displayPanels, kwh) : update("battery", kwh))}
                tagFor={(kwh) =>
                  Math.abs(kwh - (aiOption?.batteryKwh ?? AI_PICK_BATTERY_KWH)) < 0.06 ? "AI PICK" : ""
                }
              />
              <div className="flex flex-col gap-1 border-t border-[#ECEAE3] pt-3">
                <span className="pb-1 font-bold">Your future plans</span>
                <ToggleRow label="Planning to get an EV" sub="About 6,000 miles a year" checked={inputs.ev} onChange={(v) => update("ev", v)} />
                <ToggleRow label="Planning a heat pump" sub="Replacing a gas boiler" checked={inputs.heatPump} onChange={(v) => update("heatPump", v)} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <MiniStat label="System price" value={gbp(plan.systemPrice)} sub={`${plan.roiPct}% year-1 return`} />
                <MiniStat label="25-yr savings" value={gbp(plan.net25)} sub="After system cost" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border border-border p-4">
                  <div className="font-bold">EPC rating</div>
                  <div className="font-display text-2xl font-bold">
                    {plan.epcBefore} → <span className="text-green-700">{plan.epcAfter}</span>
                  </div>
                </div>
                <div className="rounded-xl border border-border p-4">
                  <div className="font-bold">Home value uplift</div>
                  <div className="font-display text-lg font-bold leading-tight text-green-700 sm:text-xl">
                    <span className="whitespace-nowrap">+{gbp(plan.homeValueUpliftLow)}</span>{" "}
                    <span className="whitespace-nowrap">–{gbp(plan.homeValueUpliftHigh)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-border px-6 py-4 text-xs text-mutedLight sm:px-8">
          Illustrative figures for an example home. <Link href="/plan" className="font-semibold text-indigo">See the full Energy Plan →</Link>
        </div>
      </div>
    </div>
  );
}

function ExamplePlanLoading() {
  return (
    <div className="rounded-[32px] p-4 dotted-indigo sm:p-8">
      <div className="overflow-hidden rounded-3xl bg-white">
        <div className="flex flex-col gap-1 border-b border-navyLight bg-navy px-6 py-4 text-white sm:px-8">
          <span className="font-display text-xl font-bold tracking-tight sm:text-2xl">Your Powlo Energy Plan</span>
          <span className="text-sm text-[#C9D1DE]">
            <span className="font-bold text-lime">Hello</span>
            {" · Example: detached home, South East"}
          </span>
        </div>
        <p className="px-6 py-16 text-center text-sm text-muted">Loading packages…</p>
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  sub,
  checked,
  onChange,
}: {
  label: string;
  sub: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex min-h-[52px] items-center justify-between gap-4 text-left"
    >
      <span>
        <span className="block font-semibold">{label}</span>
        <span className="text-xs text-muted">{sub}</span>
      </span>
      <span className={`flex h-7 w-[50px] shrink-0 items-center rounded-full p-[3px] transition-colors ${checked ? "bg-indigo" : "bg-[#D4D2CB]"}`}>
        <span className={`block h-5 w-5 rounded-full shadow-sm transition-transform ${checked ? "translate-x-[22px] bg-white" : "translate-x-0 bg-ink"}`} />
      </span>
    </button>
  );
}

function Stat({ label, value, sub, dark }: { label: string; value: string; sub: string; dark?: boolean }) {
  return (
    <div className={`rounded-2xl p-4 ${dark ? "bg-navy text-white" : "border border-border"}`}>
      <div className={`text-xs ${dark ? "text-[#C9D1DE]" : "text-muted"}`}>{label}</div>
      <div className={`font-display text-2xl font-bold tracking-tight ${dark ? "text-lime" : ""}`}>{value}</div>
      <div className={`text-xs ${dark ? "text-[#C9D1DE]" : "text-mutedLight"}`}>{sub}</div>
    </div>
  );
}

function MiniStat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-[#ECEAE3] bg-[#FCFBF8] p-3">
      <div className="text-xs text-muted">{label}</div>
      <div className="font-display text-lg font-bold">{value}</div>
      {sub ? <div className="text-xs text-mutedLight">{sub}</div> : null}
    </div>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function StarIcon({ color = "#D4FF00" }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill={color} aria-hidden="true">
      <path d="M12 2l2.2 7.8L22 12l-7.8 2.2L12 22l-2.2-7.8L2 12l7.8-2.2Z" />
    </svg>
  );
}

function MiniRing({ score, color }: { score: number; color: string }) {
  const circumference = 2 * Math.PI * 29;
  const dash = (score / 100) * circumference;
  return (
    <span className="relative block h-[62px] w-[62px] shrink-0">
      <svg width="62" height="62" viewBox="0 0 68 68" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="34" cy="34" r="29" fill="none" stroke="#16304F" strokeWidth="7" />
        <circle cx="34" cy="34" r="29" fill="none" stroke={color} strokeWidth="7" strokeLinecap="round" strokeDasharray={`${dash} ${circumference}`} style={{ transition: "stroke-dasharray .4s" }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-display text-lg font-bold">{score}</span>
    </span>
  );
}

function BigRing({ score, color }: { score: number; color: string }) {
  const circumference = 2 * Math.PI * 64;
  const dash = (score / 100) * circumference;
  return (
    <span className="relative block h-[180px] w-[180px]">
      <svg width="180" height="180" viewBox="0 0 160 160" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="80" cy="80" r="64" fill="none" stroke="#16304F" strokeWidth="14" />
        <circle cx="80" cy="80" r="64" fill="none" stroke={color} strokeWidth="14" strokeLinecap="round" strokeDasharray={`${dash} ${circumference}`} style={{ transition: "stroke-dasharray .4s" }} />
      </svg>
      <span className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-5xl font-bold tracking-tight text-white">{score}</span>
        <span className="text-sm text-[#8FA0B8]">/100</span>
      </span>
    </span>
  );
}
